import React, { memo, useCallback, useState } from "react";
import { LayoutChangeEvent, ScrollView, StyleSheet, Text, View } from "react-native";

import { useSlideshow } from "../hooks";
import { MediaSectionProps } from "@rista/core/customerDisplay";
import { MediaSlide } from "../MediaSlide";
import { EmptyCartIllustration } from "../EmptyCartIllustration";

export const MediaSection = memo(function MediaSection({ media, dwellSeconds }: MediaSectionProps) {
  if (!media.length) {
    return <IdleView />;
  }

  return <AnimatedSlideshow media={media} dwellSeconds={dwellSeconds} />;
});

const IdleView = memo(function IdleView() {
  return (
    <View style={styles.idle}>
      <View style={styles.emptyState}>
        <EmptyCartIllustration />

        <Text style={styles.emptyMessage}>
          Your cart is empty. Start adding your favorite dishes now!
        </Text>
      </View>
    </View>
  );
});

const AnimatedSlideshow = memo(function AnimatedSlideshow({
  media,
  dwellSeconds,
}: MediaSectionProps) {
  const { scrollViewRef, containerWidth, setContainerWidth, currentIndex, slideshowMedia } =
    useSlideshow({
      media,
      dwellSeconds,
    });
  const [containerHeight, setContainerHeight] = useState(0);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      setContainerWidth(event.nativeEvent.layout.width);
      setContainerHeight(event.nativeEvent.layout.height);
    },
    [setContainerWidth],
  );

  return (
    <View style={styles.slideshowWrapper} onLayout={handleLayout}>
      {containerWidth > 0 && containerHeight > 0 && (
        <ScrollView
          ref={scrollViewRef}
          horizontal
          pagingEnabled
          scrollEnabled={false}
          showsHorizontalScrollIndicator={false}
          style={styles.flexOne}
          contentContainerStyle={styles.slideshowContent}
        >
          {slideshowMedia.map((item, index) => (
            <View
              key={`${item.id ?? index}-${index}`}
              style={[styles.slideCard, { width: containerWidth, height: containerHeight }]}
            >
              <MediaSlide item={item} />
            </View>
          ))}
        </ScrollView>
      )}

      {media.length > 1 && <IndicatorView total={media.length} currentIndex={currentIndex} />}
    </View>
  );
});

const IndicatorView = memo(function IndicatorView({
  total,
  currentIndex,
}: {
  total: number;
  currentIndex: number;
}) {
  return (
    <View style={styles.indicatorWrapper}>
      <View style={styles.indicatorContainer}>
        {Array.from({ length: total }).map((_, index) => (
          <View
            key={index}
            style={[styles.indicatorPill, index === currentIndex && styles.activeIndicatorPill]}
          />
        ))}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  flexOne: {
    flex: 1,
  },

  slideshowWrapper: {
    flex: 1,
    height: "100%",
  },

  slideshowContent: {
    height: "100%",
  },

  slideCard: {
    height: "100%",
    backgroundColor: "#FFFFFF",
  },

  slideImage: {
    width: "100%",
    height: "100%",
  },

  welcome: {
    color: "#9AA0B0",
    fontSize: 56,
    fontWeight: "300",
    letterSpacing: 4,
  },

  indicatorWrapper: {
    position: "absolute",
    bottom: 28,
    left: 0,
    right: 0,
    alignItems: "center",
  },

  indicatorContainer: {
    flexDirection: "row",
    columnGap: 12,
  },

  // capsules rather than dots — borderRadius is half the height so the caps
  // stay semicircular whatever the width
  indicatorPill: {
    height: 10,
    width: 28,
    borderRadius: 5,
    backgroundColor: "rgba(255, 255, 255, 0.4)",
  },

  activeIndicatorPill: {
    backgroundColor: "#2B52DD",
  },
  
  idle: {
    height: "100%",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },

  emptyState: {
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
  },

  emptyMessage: {
    fontFamily: "Inter",
    fontSize: 16,
    fontWeight: "400",
    lineHeight: 16,
    letterSpacing: 0,
    color: "#000000",
    textAlign: "center",
  },
});
